import React, { useEffect, useState } from "react";
import {
    Avatar, Box, Card, Grid, Typography, CircularProgress
} from "@mui/material";

import GroupIcon from "@mui/icons-material/Group";
import PlaceIcon from "@mui/icons-material/Place";
import HotelIcon from "@mui/icons-material/Hotel";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { LocalOffer, ShoppingBag, AltRoute, TravelExplore, Dashboard } from "@mui/icons-material";
import LocationCityIcon from "@mui/icons-material/LocationCity";

import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { get_dashboard_admin } from "../api/API";
import { GREEN, SectionTitle } from "./AdminPageLayout";

const CardItem = ({ item }) => (
    <Card
        sx={{
            p: 2.5,
            height: "100%",
            borderRadius: 4,
            bgcolor: "#fff",
            border: "1px solid #e8efe2",
            boxShadow: "0 2px 10px rgba(46,125,50,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 2,
            transition: "transform 0.25s ease, box-shadow 0.25s ease",
            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 24px rgba(46,125,50,0.16)",
            },
        }}
    >
        <Avatar sx={{ width: 52, height: 52, bgcolor: `${item.color}1a`, color: item.color, borderRadius: 3 }}>
            {item.icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={800} lineHeight={1.1} color="text.primary">
                {item.count}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mt: 0.5, lineHeight: 1.35 }}>
                {item.label}
            </Typography>
        </Box>
    </Card>
);

function AdminDashboard() {
    const token = localStorage.getItem("token");
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await axios.get(get_dashboard_admin, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                setDashboardData(res.data);
            } catch (err) {
                console.error("Error loading dashboard:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token]);

    const generateItems = () => {
        if (!dashboardData) return [];

        return {
            systemData: [
                { label: "ผู้ใช้งานทั้งหมด", count: dashboardData.users_count, icon: <GroupIcon />, color: "#1e88e5" },
                { label: "อำเภอ", count: dashboardData.district_count, icon: <LocationCityIcon />, color: "#2e7d32" },
                { label: "เส้นทาง", count: dashboardData.travel_count, icon: <AltRoute />, color: "#00897b" },
            ],
            placesData: [
                { label: "สถานที่ท่องเที่ยวทั้งหมด", count: dashboardData.places_count, icon: <TravelExplore />, color: "#2e7d32" },
                { label: "สถานที่ท่องเที่ยว", count: dashboardData.tourist_attraction_count, icon: <PlaceIcon />, color: "#43a047" },
                { label: "ที่พัก/โฮมสเตย์", count: dashboardData.hotel_count, icon: <HotelIcon />, color: "#5e35b1" },
                { label: "ร้านอาหาร", count: dashboardData.restaurant_count, icon: <RestaurantIcon />, color: "#ef6c00" },
                { label: "ร้านของที่ระลึก", count: dashboardData.souvenir_count, icon: <ShoppingBag />, color: "#d81b60" },
                { label: "ผลิตภัณฑ์ชุมชน", count: dashboardData.community_product_count, icon: <LocalOffer />, color: "#7cb342" },
            ],
        };
    };

    const dataGroups = generateItems();

    return (
        <Box sx={{ minHeight: "100vh", p: { xs: 1, md: 2 }, maxWidth: 1200, mx: "auto" }}>
            {/* หัวข้อต้อนรับ */}
            <Box
                sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 4,
                    borderRadius: 4,
                    color: "white",
                    background: `linear-gradient(135deg, ${GREEN} 0%, #43a047 100%)`,
                    boxShadow: "0 10px 30px rgba(46,125,50,0.2)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                }}
            >
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 60, height: 60 }}>
                    <Dashboard fontSize="large" />
                </Avatar>
                <Box>
                    <Typography variant="h5" fontWeight="bold">แดชบอร์ดผู้ดูแลระบบ</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        ภาพรวมข้อมูลในระบบ Let's Go Low Carbon
                    </Typography>
                </Box>
            </Box>

            {
                loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                        <CircularProgress sx={{ color: GREEN }} />
                    </Box>
                ) : (
                    <>
                        {/* 🔹 ระบบ */}
                        <SectionTitle title="ข้อมูลระบบ" />
                        <Grid container spacing={2.5} sx={{ mb: 4 }}>
                            {dataGroups?.systemData?.map((item, idx) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                    <CardItem item={item} />
                                </Grid>
                            ))}
                        </Grid>

                        {/* 🔹 สถานที่ */}
                        <SectionTitle title="สถานที่ท่องเที่ยวและบริการ" />
                        <Grid container spacing={2.5}>
                            {dataGroups?.placesData?.map((item, idx) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                    <CardItem item={item} />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )
            }
        </Box>
    );
}

export default AdminDashboard;
